import React from 'react';

import { Link } from 'react-router-dom';

import { EditOutlined, WarningOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { GetItemStockInheritanceDTO } from '@recurrency/core-api-schema/dist/items/getItemStockInheritance';
import { Tooltip } from 'antd';
import { colors } from 'theme/colors';

import { ActionButton } from 'components/Button/ActionButton';
import { MiniTableMessage } from 'components/MiniTable';

import { makePath, routePaths } from 'utils/routes';

import { ItemStockInheritanceData } from './sidePane.utils';
import { StockInheritanceDescendantBanner } from './StockInheritanceDescendantBanner';

interface StockInheritanceInfoProps {
  itemStockInheritanceData: ItemStockInheritanceData;
  onEdit: () => void;
}

export const StockInheritanceInfo: React.FC<StockInheritanceInfoProps> = ({ itemStockInheritanceData, onEdit }) => {
  const { isLoading, allAncestorData, invalidAncestors, validDescendants } = itemStockInheritanceData;

  const validAncestors = allAncestorData.items.filter((item) => !item.hasMismatchedBaseUoms);
  const hasValidAncestors = validAncestors.length > 0;

  return isLoading ? null : (
    <div>
      <StockInheritanceDescendantBanner validDescendants={validDescendants} />
      {hasValidAncestors && (
        <>
          Inheriting stock from:{' '}
          {validAncestors.map((item, index) => (
            <React.Fragment key={item.itemCode}>
              <Link to={makePath(routePaths.purchasing.itemDetails, { id: item.itemCode })}>{item.itemCode}</Link>
              {index < validAncestors.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}
        </>
      )}
      {invalidAncestors.length > 0 && (
        <div>
          Cannot inherit stock from:{' '}
          {invalidAncestors.map((item: GetItemStockInheritanceDTO, index: number) => (
            <React.Fragment key={item.itemCode}>
              <Link to={makePath(routePaths.purchasing.itemDetails, { id: item.itemCode })}>{item.itemCode}</Link>
              {index < invalidAncestors.length - 1 ? ', ' : ''}
            </React.Fragment>
          ))}
          <Tooltip title="Cannot inherit stock from these items due to incompatible units of measure.">
            <WarningOutlined
              className={css`
                margin-left: 4px;
                color: ${colors.danger[500]};
              `}
            />
          </Tooltip>
        </div>
      )}
      {validDescendants.length === 0 && !hasValidAncestors && invalidAncestors.length === 0 && (
        <MiniTableMessage message="No inheritance found" />
      )}
      <ActionButton
        label={
          <>
            <EditOutlined /> View and Edit
          </>
        }
        onClick={onEdit}
        className={css`
          margin-top: 8px;
        `}
      />
    </div>
  );
};
